import { useState, type ReactElement } from 'react';

import { Check, ChevronDown, ChevronsUpDown, Loader2, RefreshCw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Field, FieldDescription, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

import type { EmailProcessingIntegration } from '../types/integrations';
import {
  ADVANCED_FREQUENCY,
  CUSTOM_FREQUENCY,
  describeSchedule,
  FREQUENCY_OPTIONS,
  MAX_INTERVAL_MINUTES,
} from './emailProcessing.utils';

interface EmailFolder {
  id: string;
  name: string;
}

interface EmailProcessingFolderFieldProps {
  emailFolders: EmailFolder[];
  inboxFolder: string;
  isEmailFoldersError: boolean;
  isFetchingEmailFolders: boolean;
  isLoading: boolean;
  showError: boolean;
  onChange: (folderId: string) => void;
  onRefresh: () => void;
}

export const EmailProcessingFolderField = ({
  emailFolders,
  inboxFolder,
  isEmailFoldersError,
  isFetchingEmailFolders,
  isLoading,
  showError,
  onChange,
  onRefresh,
}: EmailProcessingFolderFieldProps): ReactElement => {
  const [folderPickerOpen, setFolderPickerOpen] = useState(false);
  const [folderSearch, setFolderSearch] = useState('');
  const selectedFolder = emailFolders.find((folder) => folder.id === inboxFolder);
  const filteredEmailFolders = emailFolders.filter((folder) =>
    folder.name.toLowerCase().includes(folderSearch.trim().toLowerCase())
  );

  const handleOpenChange = (open: boolean): void => {
    setFolderPickerOpen(open);
    if (!open) setFolderSearch('');
  };

  const handleFolderChange = (folderId: string): void => {
    onChange(folderId);
    setFolderPickerOpen(false);
    setFolderSearch('');
  };

  return (
    <Field data-invalid={showError || isEmailFoldersError}>
      <FieldLabel htmlFor="email-processing-folder">Etiqueta de Gmail</FieldLabel>
      <Popover open={folderPickerOpen} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            id="email-processing-folder"
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={folderPickerOpen}
            aria-invalid={showError || isEmailFoldersError}
            className="w-full justify-between font-normal"
            disabled={isLoading || isFetchingEmailFolders || emailFolders.length === 0}
          >
            <span className="truncate">
              {isFetchingEmailFolders ? 'Cargando etiquetas...' : selectedFolder?.name || 'Selecciona una etiqueta'}
            </span>
            <ChevronsUpDown className="text-text-subtle" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-(--radix-popover-trigger-width) p-2" align="start">
          <Input
            value={folderSearch}
            placeholder="Buscar etiqueta..."
            autoFocus
            onChange={(event) => setFolderSearch(event.target.value)}
          />
          <div className="max-h-56 overflow-y-auto" role="listbox">
            {filteredEmailFolders.map((folder) => (
              <button
                key={folder.id}
                type="button"
                role="option"
                aria-selected={folder.id === inboxFolder}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-bg-surface-raised"
                onClick={() => handleFolderChange(folder.id)}
              >
                <span className="flex-1 truncate">{folder.name}</span>
                {folder.id === inboxFolder && <Check className="size-4" />}
              </button>
            ))}
            {filteredEmailFolders.length === 0 && (
              <p className="px-2 py-4 text-center text-sm text-text-subtle">No se encontraron etiquetas.</p>
            )}
          </div>
        </PopoverContent>
      </Popover>
      {inboxFolder && <p className="font-mono text-xs text-text-subtle">{inboxFolder}</p>}
      <div className="flex items-center justify-between gap-3">
        <FieldDescription>Cada proyecto debe usar una etiqueta diferente.</FieldDescription>
        <Button type="button" variant="outline" size="sm" disabled={isFetchingEmailFolders} onClick={onRefresh}>
          {isFetchingEmailFolders ? <Loader2 className="animate-spin" /> : <RefreshCw />}
          Actualizar etiquetas
        </Button>
      </div>
      {showError && <FieldError>Selecciona una etiqueta.</FieldError>}
      {isEmailFoldersError && <FieldError>No se pudieron cargar las etiquetas de Gmail.</FieldError>}
      {!isFetchingEmailFolders && !isEmailFoldersError && emailFolders.length === 0 && (
        <FieldDescription>No hay etiquetas personalizadas disponibles en Gmail.</FieldDescription>
      )}

      <Collapsible>
        <CollapsibleTrigger className="group/help flex w-fit items-center gap-1 text-sm text-text-brand">
          <ChevronDown className="size-3.5 transition-transform group-data-[state=open]/help:rotate-180" />
          ¿Cómo creo la etiqueta?
        </CollapsibleTrigger>
        <CollapsibleContent>
          <ol className="mt-2 flex list-decimal flex-col gap-1 pl-5 text-sm text-text-muted">
            <li>
              Pide una dirección <code>hello+nombre-inmobiliaria@lidz.ai</code>. No uses alias: la cuenta admite un
              máximo de 25.
            </li>
            <li>En la cuenta hello de Gmail, crea la etiqueta y el filtro que dirige los correos a ella.</li>
            <li>Vuelve aquí y pulsa “Actualizar etiquetas” para que aparezca en la lista.</li>
          </ol>
          <a
            href="https://lidz-ai.monday.com/boards/18389893052"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-block text-sm text-text-brand"
          >
            Ver la guía completa en Monday
          </a>
        </CollapsibleContent>
      </Collapsible>
    </Field>
  );
};

interface EmailProcessingFrequencyFieldsProps {
  customInterval: string;
  customIntervalNumber: number;
  data: EmailProcessingIntegration;
  frequency: string;
  isLoading: boolean;
  processingSchedule: string;
  showError: boolean;
  onCustomIntervalChange: (value: string) => void;
  onFrequencyChange: (value: string) => void;
}

export const EmailProcessingFrequencyFields = ({
  customInterval,
  customIntervalNumber,
  data,
  frequency,
  isLoading,
  processingSchedule,
  showError,
  onCustomIntervalChange,
  onFrequencyChange,
}: EmailProcessingFrequencyFieldsProps): ReactElement => {
  if (!data.scheduleEditable) {
    return (
      <Field>
        <FieldLabel>Frecuencia de procesamiento</FieldLabel>
        {data.processingSchedule && (
          <p className="text-sm text-text-default">{describeSchedule(data.processingSchedule)}</p>
        )}
        <FieldDescription>
          Este proyecto usa un scheduler dedicado. La frecuencia no se cambia desde aquí.
        </FieldDescription>
      </Field>
    );
  }

  return (
    <>
      <Field data-invalid={showError}>
        <FieldLabel htmlFor="email-processing-frequency">Frecuencia de procesamiento</FieldLabel>
        <Select value={frequency} disabled={isLoading} onValueChange={onFrequencyChange}>
          <SelectTrigger id="email-processing-frequency" className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              {FREQUENCY_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
              <SelectItem value={CUSTOM_FREQUENCY}>Otra frecuencia...</SelectItem>
              {frequency === ADVANCED_FREQUENCY && (
                <SelectItem value={ADVANCED_FREQUENCY}>Configuración avanzada actual</SelectItem>
              )}
            </SelectGroup>
          </SelectContent>
        </Select>
        <FieldDescription>
          {frequency === ADVANCED_FREQUENCY
            ? `Este proyecto conserva su horario avanzado: ${data.processingSchedule}`
            : processingSchedule &&
              `Los correos se revisarán cada ${frequency === CUSTOM_FREQUENCY ? customIntervalNumber : frequency} minutos.`}
        </FieldDescription>
      </Field>

      {frequency === CUSTOM_FREQUENCY && (
        <Field data-invalid={showError}>
          <FieldLabel htmlFor="email-processing-custom-frequency">Minutos entre revisiones</FieldLabel>
          <Input
            id="email-processing-custom-frequency"
            type="number"
            min={1}
            max={MAX_INTERVAL_MINUTES}
            value={customInterval}
            placeholder="13"
            disabled={isLoading}
            aria-invalid={showError}
            onChange={(event) => onCustomIntervalChange(event.target.value)}
          />
          <FieldDescription>Ingresa un valor entre 1 y {MAX_INTERVAL_MINUTES} minutos.</FieldDescription>
          {showError && <FieldError>Ingresa una frecuencia válida.</FieldError>}
        </Field>
      )}
    </>
  );
};
